import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
interface BlogSectionProps {
  onNavigate?: (page: string) => void;
}
export function BlogSection({
  onNavigate
}: BlogSectionProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        delay: i * 0.15,
        ease: [0.22, 1, 0.36, 1]
      }
    })
  };
  return <section id="blog" className="w-full bg-[#FAF8F5] py-24 sm:py-32" ref={ref} data-id="element-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-21">
        {/* Section Header */}
        <div className="text-center mb-16" data-id="element-22">
          <motion.div initial={{
          opacity: 0
        }} animate={isInView ? {
          opacity: 1
        } : {}} transition={{
          duration: 0.8,
          ease: [0.22, 1, 0.36, 1]
        }} className="flex items-center justify-center gap-3 mb-6" data-id="element-23">
            <span className="text-[#D4A853]/40 text-xs" data-id="element-24">◆</span>
            <motion.div initial={{
            scaleX: 0
          }} animate={isInView ? {
            scaleX: 1
          } : {}} transition={{
            duration: 0.8,
            ease: [0.22, 1, 0.36, 1]
          }} className="w-16 h-0.5 bg-[#D4A853] origin-center" data-id="element-25" />
            <span className="text-[#D4A853]/40 text-xs" data-id="element-26">◆</span>
          </motion.div>
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.8,
          delay: 0.1
        }} className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4" data-id="element-27">
            Latest from Our Journal
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 16
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="text-slate-500 text-lg max-w-xl mx-auto font-light" data-id="element-28">
            Travel stories, insider tips, and inspiration for your next
            Himalayan journey
          </motion.p>
        </div>

        {/* Blog Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-29">
          {/* Blog 1 — Luxury Trekking */}
          <motion.article custom={0} variants={cardVariants} initial="hidden" animate={isInView ? 'visible' : 'hidden'} className="group cursor-pointer" onClick={() => onNavigate?.('blog')} data-id="element-30">
            <div className="block" data-id="element-31">
              <div className="relative overflow-hidden rounded-2xl aspect-[16/10] mb-5" data-id="element-32">
                <img src="https://images.unsplash.com/photo-1486911278844-a81c5267e227?w=600&q=80" alt="Luxury trekking in the Himalayas with mountain views" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out" data-id="element-33" />
                <span className="absolute top-4 left-4 px-3 py-1 bg-teal-600/90 backdrop-blur-sm text-white text-xs font-semibold rounded-full tracking-wide" data-id="element-34">
                  Trekking
                </span>
              </div>
              <div data-id="element-35">
                <time className="text-sm text-slate-400 font-medium" data-id="element-36">
                  January 8, 2026
                </time>
                <h3 className="font-playfair text-xl font-semibold text-slate-900 mt-2 mb-3 group-hover:text-[#D4A853] transition-colors duration-300" data-id="element-37">
                  The Ultimate Guide to Luxury Trekking in Nepal
                </h3>
                <p className="text-slate-500 text-sm leading-relaxed line-clamp-2 mb-4" data-id="element-38">
                  Discover how luxury trekking is redefining adventure travel in
                  the Himalayas, from heated lodges to gourmet mountain dining.
                </p>
                <span className="inline-flex items-center gap-1.5 text-sm font-medium text-[#D4A853] group-hover:gap-2.5 transition-all duration-300" data-id="element-39">
                  Read Article
                  <ArrowRightIcon className="w-4 h-4" data-id="element-40" />
                </span>
              </div>
            </div>
          </motion.article>

          {/* Blog 2 — Hidden Temples */}
          <motion.article custom={1} variants={cardVariants} initial="hidden" animate={isInView ? 'visible' : 'hidden'} className="group cursor-pointer" onClick={() => onNavigate?.('blog')} data-id="element-41">
            <div className="block" data-id="element-42">
              <div className="relative overflow-hidden rounded-2xl aspect-[16/10] mb-5" data-id="element-43">
                <img src="https://images.unsplash.com/photo-1558799401-1dcba79834c2?w=600&q=80" alt="Ancient temple architecture in Kathmandu Valley" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out" data-id="element-44" />
                <span className="absolute top-4 left-4 px-3 py-1 bg-teal-600/90 backdrop-blur-sm text-white text-xs font-semibold rounded-full tracking-wide" data-id="element-45">
                  Culture
                </span>
              </div>
              <div data-id="element-46">
                <time className="text-sm text-slate-400 font-medium" data-id="element-47">
                  December 22, 2025
                </time>
                <h3 className="font-playfair text-xl font-semibold text-slate-900 mt-2 mb-3 group-hover:text-[#D4A853] transition-colors duration-300" data-id="element-48">
                  Hidden Temples of Kathmandu Valley
                </h3>
                <p className="text-slate-500 text-sm leading-relaxed line-clamp-2 mb-4" data-id="element-49">
                  Beyond the famous Pashupatinath and Boudhanath, explore the
                  secret sacred sites that most travelers never see.
                </p>
                <span className="inline-flex items-center gap-1.5 text-sm font-medium text-[#D4A853] group-hover:gap-2.5 transition-all duration-300" data-id="element-50">
                  Read Article
                  <ArrowRightIcon className="w-4 h-4" data-id="element-51" />
                </span>
              </div>
            </div>
          </motion.article>

          {/* Blog 3 — Chitwan Safari */}
          <motion.article custom={2} variants={cardVariants} initial="hidden" animate={isInView ? 'visible' : 'hidden'} className="group cursor-pointer" onClick={() => onNavigate?.('blog')} data-id="element-52">
            <div className="block" data-id="element-53">
              <div className="relative overflow-hidden rounded-2xl aspect-[16/10] mb-5" data-id="element-54">
                <img src="https://images.unsplash.com/photo-1549366021-9f761d450615?w=600&q=80" alt="Wildlife safari experience in Chitwan National Park" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out" data-id="element-55" />
                <span className="absolute top-4 left-4 px-3 py-1 bg-teal-600/90 backdrop-blur-sm text-white text-xs font-semibold rounded-full tracking-wide" data-id="element-56">
                  Wildlife
                </span>
              </div>
              <div data-id="element-57">
                <time className="text-sm text-slate-400 font-medium" data-id="element-58">
                  November 30, 2025
                </time>
                <h3 className="font-playfair text-xl font-semibold text-slate-900 mt-2 mb-3 group-hover:text-[#D4A853] transition-colors duration-300" data-id="element-59">
                  A Day on Safari in Chitwan National Park
                </h3>
                <p className="text-slate-500 text-sm leading-relaxed line-clamp-2 mb-4" data-id="element-60">
                  From one-horned rhinos to elusive Bengal tigers, experience
                  the thrill of Nepal's premier wildlife sanctuary in style.
                </p>
                <span className="inline-flex items-center gap-1.5 text-sm font-medium text-[#D4A853] group-hover:gap-2.5 transition-all duration-300" data-id="element-61">
                  Read Article
                  <ArrowRightIcon className="w-4 h-4" data-id="element-62" />
                </span>
              </div>
            </div>
          </motion.article>
        </div>
      </div>
    </section>;
}